import React, { Suspense } from 'react';
import { createRoot } from 'react-dom/client';

// Fontsource fonts
import '@fontsource/open-sans/300.css';
import '@fontsource/open-sans/400.css';
import '@fontsource/open-sans/600.css';
import '@fontsource/open-sans/700.css';
import '@fontsource/montserrat/400.css';
import '@fontsource/montserrat/600.css';
import '@fontsource/montserrat/700.css';
import '@fontsource/montserrat/800.css';

// Side-effect import: kicks off i18next.init() so the dictionary is loading
// by the time React mounts. The <Suspense> boundary below catches the
// initial-bundle promise that useTranslation() throws on first render.
// See src/i18n/index.ts.
import './i18n';
import App from './App';
import { AppErrorBoundary } from './components/ui/AppErrorBoundary';
import { SnagScreen } from './components/ui/SnagScreen';
// Marketing site styles — Poppins @font-face, brand tokens, layout
// containers, nav/footer/hero/section component styles, utility classes.
//
// MUST be imported AFTER `App` so it lands LAST in the cascade. App.tsx
// imports `global.scss` at its top, which runs `@tailwind base` (its
// preflight resets h1–h6 sizes to 0). Importing site.css here, AFTER
// App, makes its heading/body rules win the same-specificity cascade.
import './styles/site.css';

// Ybug bug-report widget. Vanilla `<script>` snippet from ybug.io's install
// page. Gated by env vars — `VITE_SHOW_YBUG=true` + a non-empty
// `VITE_YBUG_ID` (matches the sibling apps' convention; today STG has both
// set, dev/prod/local don't).
const YBUG_ID = import.meta.env.VITE_YBUG_ID;
if (import.meta.env.VITE_SHOW_YBUG === 'true' && YBUG_ID) {
  (function () {
    (window as unknown as { ybug_settings: { id: string } }).ybug_settings = {
      id: YBUG_ID,
    };
    const ybug = document.createElement('script');
    ybug.type = 'text/javascript';
    ybug.async = true;
    ybug.src = 'https://widget.ybug.io/button/' + YBUG_ID + '.js';
    const s = document.getElementsByTagName('script')[0];
    s?.parentNode?.insertBefore(ybug, s);
  })();
}

// www is intentionally service-worker-free: every refresh fetches fresh
// HTML/CSS/JS from the CDN, so there's no SW update flow to coordinate and
// no precache to bust. Push notifications + Firebase Messaging are also
// gone — this app is a mostly-public marketing site, not a notification
// target.

const container = document.getElementById('root');
const root = createRoot(container!);

try {
  root.render(
    <React.StrictMode>
      <AppErrorBoundary>
        {/* Suspense covers the i18n http-backend's first-bundle load.
            Fallback is null because the index.html splash already paints
            the boot frame — a separate spinner here would just flash on
            top of it. Per-screen lazy bundles get their own inner
            Suspense in App.tsx. */}
        <Suspense fallback={null}>
          <App />
        </Suspense>
      </AppErrorBoundary>
    </React.StrictMode>
  );
} catch {
  // If the very first synchronous render throws (something exploded before
  // the ErrorBoundary could mount), fall back to the bare SnagScreen so the
  // user gets a reload affordance instead of a blank page that Safari will
  // eventually condemn as "A problem repeatedly occurred."
  root.render(<SnagScreen />);
}
